import React from "react";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { Trans } from "@coworker/locales";
import styled from "styled-components";
import { useInputPopup } from "../../InputPopup";
import { updateQuestionRequest } from "../hooks/useMFAQsService";
import {
  getTranslatedQuestionText,
  removeQuestionMarks,
} from "./QuestionsDetailPage.helper";
import { Question } from "../BackendApiRepository/QuestionResponse";
import { useUpdateTaskMutation } from "@coworker/apprestructured/src/tasks/hooks/useUpdateTaskById";

interface QuestionActionButtonRowComponentProps {
  questionId: string;
  questionText: string;
  setQuestion: React.Dispatch<React.SetStateAction<Question | undefined>>;
  taskId?: string;
  mergeQuestionButtonClicked: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenMergeQuestionModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const ButtonContainer = styled("div")`
  display: flex;
  padding-inline: 16px;
  padding-block: 24px;
  gap: 8px;
  border-top: 1px solid var(--grey200);
  border-bottom: 1px solid var(--grey200);
`;

const QuestionActionButtonRowComponent: React.FC<
  QuestionActionButtonRowComponentProps
> = ({
  questionId,
  setQuestion,
  questionText,
  mergeQuestionButtonClicked,
  taskId,
  setOpenMergeQuestionModal,
}) => {
  const { getInput, closePopup } = useInputPopup();

  const updateTask = useUpdateTaskMutation();

  const updateQuestion = async (newQuestionText: string) => {
    const subject = removeQuestionMarks(newQuestionText);
    const questionText = getTranslatedQuestionText(newQuestionText);
    const payload = {
      question_id: questionId,
      question_text: questionText,
      subject: subject,
    };

    const data = { question_text: questionText };

    try {
      if (taskId) {
        await updateTask.mutateAsync({ data, taskId });
        await updateQuestionRequest(questionId, questionText);
      } else {
        await updateQuestionRequest(questionId, questionText);
      }
    } catch (error) {
      console.error("Error updating task:", error);
    }

    setQuestion((prevQuestion: Question | undefined) => {
      if (!prevQuestion) return prevQuestion;
      return {
        ...prevQuestion,
        ...payload,
      };
    });
  };

  return (
    <ButtonContainer>
      <FixaButton
        data-testid="createMFAQTask"
        type="secondary"
        text={<Trans>{"mfaqEditQuestionString"}</Trans>}
        size="small"
        onClick={async () => {
          const newQuestionText = await getInput("mfaqEditQuestion", {
            questionText,
          });
          if (!newQuestionText) return;
          updateQuestion(newQuestionText);
          closePopup();
        }}
      />
      <FixaButton
        data-testid="mergeMFAQQuestion"
        type="secondary"
        text={<Trans>{"mfaqMergeQuestionString"}</Trans>}
        size="small"
        onClick={() => {
          mergeQuestionButtonClicked(true);
          setOpenMergeQuestionModal(true);
        }}
      />
    </ButtonContainer>
  );
};

export default QuestionActionButtonRowComponent;
